import { atom } from "jotai";
import { analytics } from "@heycatch/sdk";
import {
  tasksAtom,
  allTasksAtom,
  preferencesAtom,
  completedTasksAtom,
  activeSlateAtom,
  slatesAtom,
  showCompletedAtom,
  completedFilterAtom,
  hasRunAtom,
  subscriptionTierAtom,
  subscriptionEndAtom,
  referralCodeAtom,
  isDemoAtom,
  emailAtom,
  popPositionAtom,
  popAtom,
  timerAtom,
  elapsedTimeAtom,
  activeBubbleAtom,
  categoryImagesAtom,
  currentProjectIdAtom,
  recurringRulesAtom,
} from "./atoms/index.js";
import {
  makeAuthenticatedRequest,
  checkAuthStatus,
} from "../helpers/authUtils";
import { baseBackendURL } from "../helpers/baseUrl";

// Fetch Tasks Action
export const fetchTasksAtom = atom(null, async (get, set) => {
  try {
    const activeSlate = get(activeSlateAtom);
    const slates = get(slatesAtom);
    const showCompleted = get(showCompletedAtom);
    const completedFilter = get(completedFilterAtom);
    const currentProjectId = get(currentProjectIdAtom);

    const filter = showCompleted ? completedFilter : "pending";
    const parentParam =
      currentProjectId !== null
        ? `&parentId=${currentProjectId}`
        : "&parentId=null";

    const [
      tasksResponse,
      allTasksResponse,
      preferencesResponse,
      completedTasksResponse,
      recurringRulesData,
    ] = await Promise.all([
      makeAuthenticatedRequest(
        `${baseBackendURL}/tasks?slate=${slates[activeSlate]}&filter=${filter}${parentParam}`
      ),
      makeAuthenticatedRequest(
        `${baseBackendURL}/tasks?slate=${slates[activeSlate]}&filter=${filter}&parentId=all`
      ),
      makeAuthenticatedRequest(
        `${baseBackendURL}/preferences?slate=${slates[activeSlate]}`
      ),
      makeAuthenticatedRequest(
        `${baseBackendURL}/tasks-completed?slate=${slates[activeSlate]}`
      ),
      makeAuthenticatedRequest(`${baseBackendURL}/recurringTasks`)
        .then((r) => r.json())
        .catch((err) => {
          if (err.message?.includes("Authentication required")) throw err;
          return { success: false };
        }),
    ]);
    const [tasksData, allTasksData, preferencesData, completedTasksData] =
      await Promise.all([
        tasksResponse.json(),
        allTasksResponse.json(),
        preferencesResponse.json(),
        completedTasksResponse.json(),
      ]);

    set(hasRunAtom, false);

    if (tasksData.success && allTasksData.success) {
      set(tasksAtom, tasksData.tasks);
      set(allTasksAtom, allTasksData.tasks);
      if (preferencesData.success) {
        set(preferencesAtom, preferencesData.preferences);
      }
      if (completedTasksData.success) {
        set(completedTasksAtom, completedTasksData);
      }
      if (recurringRulesData.success) {
        set(recurringRulesAtom, recurringRulesData.rules);
      }
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    if (error.message?.includes("Authentication required")) {
      throw new Error("Authentication required");
    }
  }
});

// Check Auth Action
export const checkAuthAtom = atom(null, async (get, set) => {
  const { isAuthenticated, userData } = await checkAuthStatus();
  if (isAuthenticated) {
    set(subscriptionTierAtom, userData.subscription_tier);
    set(subscriptionEndAtom, userData.subscription_end);
    set(referralCodeAtom, userData.referral_code);
    set(isDemoAtom, userData.is_demo);
    set(emailAtom, userData.email);
    analytics.setIdentity(String(userData.user_id), {
      email: userData.email,
      plan: userData.subscription_tier === 2 ? "pro" : userData.subscription_tier === 1 ? "plus" : "free",
    });
    return true;
  }
  analytics.resetIdentity();
  return false;
});

// Complete Task Action
export const completeTaskAtom = atom(null, async (get, set, task) => {
  try {
    const response = await makeAuthenticatedRequest(
      `${baseBackendURL}/completeTask`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selectedTask: task.task_id }),
      }
    );

    const data = await response.json();
    if (data.success) {
      set(popPositionAtom, {
        x: task.x - 300,
        y: task.y,
        color: task.category_color,
      });

      // Trigger pop animation
      set(popAtom, true);
      setTimeout(() => set(popAtom, false), 2000);

      // Clear timer if active
      const timer = get(timerAtom);
      if (timer) {
        clearInterval(timer);
        set(timerAtom, null);
        set(elapsedTimeAtom, 0);
        set(activeBubbleAtom, null);
      }

      // Refresh tasks
      setTimeout(() => set(fetchTasksAtom), 1000);

      return true;
    } else {
      console.error(data.message || "Failed to complete task");
      return false;
    }
  } catch (error) {
    console.error("Error completing task:", error);
    if (error.message?.includes("Authentication required")) {
      throw new Error("Authentication required");
    }
    return false;
  }
});

// Add Task Action
export const addTaskAtom = atom(null, async (get, set, taskData) => {
  try {
    const activeSlate = get(activeSlateAtom);
    const slates = get(slatesAtom);

    const response = await makeAuthenticatedRequest(
      `${baseBackendURL}/addTask`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...taskData,
          slate: slates[activeSlate],
        }),
      }
    );

    const data = await response.json();
    if (data.task_id) {
      set(fetchTasksAtom);
      return data.task_id;
    }
    return null;
  } catch (error) {
    console.error("Error adding task:", error);
    if (error.message?.includes("Authentication required")) {
      throw new Error("Authentication required");
    }
    return null;
  }
});

// Update Task Action
export const updateTaskAtom = atom(null, async (get, set, updatedTask) => {
  try {
    const activeSlate = get(activeSlateAtom);
    const slates = get(slatesAtom);

    const response = await makeAuthenticatedRequest(
      `${baseBackendURL}/editTask`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...updatedTask,
          slate: slates[activeSlate],
        }),
      }
    );

    const data = await response.json();
    if (data.success) {
      set(fetchTasksAtom);
      return true;
    } else {
      console.error(data.message || "Failed to update task");
      return false;
    }
  } catch (error) {
    console.error("Error updating task:", error);
    if (error.message?.includes("Authentication required")) {
      throw new Error("Authentication required");
    }
    return false;
  }
});

// Duplicate Task Action
export const duplicateTaskAtom = atom(null, async (get, set, taskId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `${baseBackendURL}/duplicateTask`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalTaskId: taskId }),
      }
    );

    const data = await response.json();
    if (data.success) {
      set(fetchTasksAtom);
      return true;
    } else {
      console.error(data.message || "Failed to duplicate task");
      return false;
    }
  } catch (error) {
    console.error("Error duplicating task:", error);
    if (error.message?.includes("Authentication required")) {
      throw new Error("Authentication required");
    }
    return false;
  }
});

// Logout Action
export const logoutAtom = atom(null, async (get, set) => {
  set(tasksAtom, []);
  try {
    await fetch(`${baseBackendURL}/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    console.error("Error logging out:", error);
  }
  // Always redirect to login page after logout
  analytics.resetIdentity();
  window.location.href = "/";
  return true;
});

// Generate Category Images Action
export const generateCategoryImagesAtom = atom(null, (get, set) => {
  const preferences = get(preferencesAtom);
  const activeSlate = get(activeSlateAtom);
  const slates = get(slatesAtom);

  const slatePreferences = preferences.filter(
    (pref) => pref.slate === slates[activeSlate] && pref.category !== 0
  );

  const images = slatePreferences.reduce((acc, pref) => {
    const imageName = `bubble_${(pref.color || "default").toLowerCase()}.png`;
    acc[pref.category] = `${process.env.PUBLIC_URL}/media/${imageName}`;
    return acc;
  }, {});

  images.default = `${process.env.PUBLIC_URL}/media/bubble_default.png`;

  set(categoryImagesAtom, images);
  return images;
});

// Task Hierarchy Navigation Actions
export const navigateToProjectAtom = atom(null, (get, set, projectId) => {
  set(currentProjectIdAtom, projectId);
  set(hasRunAtom, false);
  set(fetchTasksAtom);
});

export const navigateToTopLevelAtom = atom(null, (get, set) => {
  set(currentProjectIdAtom, null);
  set(hasRunAtom, false);
  set(fetchTasksAtom);
});

// Navigate to specific level in hierarchy
export const navigateToLevelAtom = atom(null, (get, set, { id, level }) => {
  if (level === 0) {
    set(currentProjectIdAtom, null);
  } else {
    set(currentProjectIdAtom, id);
  }
  set(hasRunAtom, false);
  set(fetchTasksAtom);
});

// Complete Task with Children Action
export const completeTaskWithChildrenAtom = atom(
  null,
  async (get, set, { taskId, completeChildren = false }) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${baseBackendURL}/completeTask`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            selectedTask: taskId,
            completeChildren: completeChildren,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        set(fetchTasksAtom);
        return true;
      } else {
        console.error(data.message || "Failed to complete task");
        return false;
      }
    } catch (error) {
      console.error("Error completing task:", error);
      if (error.message?.includes("Authentication required")) {
        throw new Error("Authentication required");
      }
      return false;
    }
  }
);

// Save Recurring Task Action
export const saveRecurringTaskAtom = atom(
  null,
  async (get, set, { taskId, type, config }) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${baseBackendURL}/recurringTask`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskId, type, config }),
        }
      );
      const data = await response.json();
      if (data.success) {
        set(fetchTasksAtom);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving recurring task:", error);
      return false;
    }
  }
);

// Delete Recurring Task Action
export const deleteRecurringTaskAtom = atom(
  null,
  async (get, set, taskId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${baseBackendURL}/recurringTask/${taskId}`,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data.success) {
        set(fetchTasksAtom);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error deleting recurring task:", error);
      return false;
    }
  }
);

// Group Tasks Action
export const groupTasksAtom = atom(
  null,
  async (get, set, { taskIds, parentId }) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${baseBackendURL}/groupTasks`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskIds, parentId }),
        }
      );

      const data = await response.json();
      if (data.success) {
        set(fetchTasksAtom);
        return data.affectedRows;
      } else {
        console.error(data.message || "Failed to group tasks");
        return false;
      }
    } catch (error) {
      console.error("Error grouping tasks:", error);
      if (error.message?.includes("Authentication required")) {
        throw new Error("Authentication required");
      }
      return false;
    }
  }
);
